import React, { useState, useEffect } from "react";
import {
  getAllAssessments,
  Assessment,
} from "../../services/AssessmentEligibilityService";
import {
  getAllDomains,
  getAssessmentDomains,
  addDomain,
  bulkAddDomains,
  deleteDomain,
  updateDomain,
  AssessmentDomain,
} from "../../services/AssessmentDomainService";
import { CircularProgress } from "@mui/material";
import "./ManageAssessmentDomains.css";
import { Layout } from "@/layout/Layout";
import { useAppTheme } from "@contexts/ThemeContext";

const ManageAssessmentDomains: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  // View mode: 'list' shows all domains table, 'add' shows add domain form
  const [viewMode, setViewMode] = useState<"list" | "add">("list");

  // All domains table states
  const [allDomains, setAllDomains] = useState<AssessmentDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Add domain form states
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] =
    useState<Assessment | null>(null);
  const [domains, setDomains] = useState<AssessmentDomain[]>([]);
  const [activeTab, setActiveTab] = useState<"view" | "add">("view");
  const [assessmentSearchTerm, setAssessmentSearchTerm] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Add domain input states
  const [singleDomain, setSingleDomain] = useState<string>("");
  const [bulkDomains, setBulkDomains] = useState<string>("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [singleDomainError, setSingleDomainError] = useState<string>("");

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    domainId: number | null;
    domainName: string;
  }>({ isOpen: false, domainId: null, domainName: "" });

  const [resultModal, setResultModal] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  }>({ isOpen: false, type: "success", title: "", message: "" });

  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    domainId: number | null;
    domainName: string;
    assessmentId: number | null;
  }>({ isOpen: false, domainId: null, domainName: "", assessmentId: null });

  useEffect(() => {
    loadAllDomains();
    loadAssessments();
  }, []);

  const loadAllDomains = async () => {
    setLoading(true);
    try {
      const response = await getAllDomains();
      if (response.success && response.data) {
        setAllDomains(response.data.domains || []);
      }
    } catch (error) {
      console.error("Failed to load all domains:", error);
      setAllDomains([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAssessments = async () => {
    try {
      const response = await getAllAssessments();
      if (response.success && response.data) {
        setAssessments(response.data);
      }
    } catch (error) {
      console.error("Failed to load assessments:", error);
    }
  };

  const handleAssessmentSelect = async (assessment: Assessment) => {
    setSelectedAssessment(assessment);
    setAssessmentSearchTerm("");
    setIsDropdownOpen(false);
    setActiveTab("view");
    setSingleDomainError("");
    loadDomainsForAssessment(assessment.assessmentId);
  };

  const loadDomainsForAssessment = async (assessmentId: number) => {
    setLoading(true);
    try {
      const response = await getAssessmentDomains(assessmentId);
      if (response.success && response.data) {
        setDomains(response.data.domains);
      } else {
        setDomains([]);
      }
    } catch (error) {
      setDomains([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and paginate all domains for list view
  const filteredDomains = allDomains.filter(
    (domain) =>
      domain.domain_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (domain.assessmentTitle &&
        domain.assessmentTitle
          .toLowerCase()
          .includes(searchTerm.toLowerCase())),
  );

  const totalPages = Math.ceil(filteredDomains.length / itemsPerPage);
  const paginatedDomains = filteredDomains.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Filter assessments for add domain dropdown
  const filteredAssessments = assessments.filter(
    (assessment) =>
      assessment.assessmentTitle
        .toLowerCase()
        .includes(assessmentSearchTerm.toLowerCase()) ||
      assessment.course
        .toLowerCase()
        .includes(assessmentSearchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".search-select-wrapper")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClearSelection = () => {
    setSelectedAssessment(null);
    setDomains([]);
    setAssessmentSearchTerm("");
    setSingleDomain("");
    setBulkDomains("");
    setSingleDomainError("");
    setActiveTab("view");
  };

  const handleSingleDomainChange = (value: string) => {
    setSingleDomain(value);

    // Clear error when input is empty
    if (!value.trim()) {
      setSingleDomainError("");
      return;
    }

    // Validate in real-time
    const validation = validateDomain(value);
    if (!validation.isValid) {
      setSingleDomainError(validation.error || "");
    } else {
      setSingleDomainError("");
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedAssessment(null);
    setDomains([]);
    setAssessmentSearchTerm("");
    setSingleDomain("");
    setBulkDomains("");
    setSingleDomainError("");
    setActiveTab("view");
    loadAllDomains();
  };

  const validateDomain = (domain: string): { isValid: boolean; error?: string } => {
    const trimmedDomain = domain.trim();

    if (!trimmedDomain) {
      return { isValid: false, error: "Domain cannot be empty." };
    }

    // Domain should start with @ if provided, or we can add it
    const domainToValidate = trimmedDomain.startsWith('@')
      ? trimmedDomain.slice(1)
      : trimmedDomain;

    // Check if domain is empty after removing @
    if (!domainToValidate) {
      return { isValid: false, error: "Please enter a valid domain after the @ symbol (e.g., @example.com)" };
    }

    // Basic domain validation regex
    // Allows: example.com, sub.example.com, etc.
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    if (!domainRegex.test(domainToValidate)) {
      return {
        isValid: false,
        error: "Invalid domain format. Please enter a valid domain (e.g., @example.com, @company.org)"
      };
    }

    return { isValid: true };
  };

  const handleAddSingleDomain = async () => {
    if (!selectedAssessment || !singleDomain.trim()) {
      setResultModal({
        isOpen: true,
        type: "error",
        title: "Missing Information",
        message: "Please enter a domain name before submitting.",
      });
      return;
    }

    // Validate domain format
    const validation = validateDomain(singleDomain);
    if (!validation.isValid) {
      setResultModal({
        isOpen: true,
        type: "error",
        title: "Invalid Domain",
        message: validation.error || "Please enter a valid domain.",
      });
      return;
    }

    setAddingDomain(true);
    try {
      const domainToAdd = singleDomain.trim().startsWith('@')
        ? singleDomain.trim()
        : '@' + singleDomain.trim();

      const response = await addDomain(
        selectedAssessment.assessmentId,
        domainToAdd,
      );
      if (response.success) {
        setResultModal({
          isOpen: true,
          type: "success",
          title: "Domain Added Successfully",
          message: `Domain "${domainToAdd}" has been added to this assessment.`,
        });
        setSingleDomain("");
        setSingleDomainError("");
        loadDomainsForAssessment(selectedAssessment.assessmentId);
      } else {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Failed to Add Domain",
          message:
            (response.error as string) ||
            "An error occurred while adding the domain. Please check if the domain already exists.",
        });
      }
    } catch (error: any) {
      let errorMessage = "An unexpected error occurred. Please try again.";

      if (error?.response?.status === 400) {
        errorMessage = "Invalid domain format or the domain already exists.";
      } else if (error?.response?.status === 409) {
        errorMessage = "This domain has already been added to the assessment.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setResultModal({
        isOpen: true,
        type: "error",
        title: "Operation Failed",
        message: errorMessage,
      });
    } finally {
      setAddingDomain(false);
    }
  };

  const handleBulkAddDomains = async () => {
    if (!selectedAssessment || !bulkDomains.trim()) {
      setResultModal({
        isOpen: true,
        type: "error",
        title: "Missing Information",
        message: "Please enter at least one domain before submitting.",
      });
      return;
    }

    setAddingDomain(true);
    try {
      const domainList = bulkDomains
        .split(/[,\n]+/)
        .map((d) => d.trim())
        .filter((d) => d.length > 0);

      if (domainList.length === 0) {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Invalid Input",
          message: "Please enter valid domain names.",
        });
        setAddingDomain(false);
        return;
      }

      // Validate all domains
      const invalidDomains: string[] = [];
      const validDomains: string[] = [];

      domainList.forEach((domain) => {
        const validation = validateDomain(domain);
        if (validation.isValid) {
          const domainToAdd = domain.startsWith('@') ? domain : '@' + domain;
          validDomains.push(domainToAdd);
        } else {
          invalidDomains.push(domain);
        }
      });

      if (invalidDomains.length > 0 && validDomains.length === 0) {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Invalid Domains",
          message: `All domains are invalid. Please enter valid domain names (e.g., @example.com, @company.org). Invalid domains: ${invalidDomains.slice(0, 3).join(', ')}${invalidDomains.length > 3 ? '...' : ''}`,
        });
        setAddingDomain(false);
        return;
      }

      const response = await bulkAddDomains(
        selectedAssessment.assessmentId,
        validDomains,
      );
      if (response.success) {
        const data = response.data;
        let message = `Added ${data?.addedCount || 0} domain(s).`;

        if (data?.skippedCount > 0) {
          message += ` ${data.skippedCount} already existed.`;
        }

        if (invalidDomains.length > 0) {
          message += ` ${invalidDomains.length} domain(s) were skipped due to invalid format: ${invalidDomains.slice(0, 3).join(', ')}${invalidDomains.length > 3 ? '...' : ''}`;
        }

        setResultModal({
          isOpen: true,
          type: invalidDomains.length > 0 ? "error" : "success",
          title: invalidDomains.length > 0 ? "Partially Completed" : "Domains Added Successfully",
          message: message,
        });
        setBulkDomains("");
        loadDomainsForAssessment(selectedAssessment.assessmentId);
      } else {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Failed to Add Domains",
          message:
            (response.error as string) ||
            "An error occurred while adding domains. Please check your input and try again.",
        });
      }
    } catch (error: any) {
      let errorMessage = "An unexpected error occurred. Please try again.";

      if (error?.response?.status === 400) {
        errorMessage = "Invalid domain format. Please ensure all domains are in the correct format (e.g., @example.com).";
      } else if (error?.response?.status === 409) {
        errorMessage = "Some or all domains already exist for this assessment.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setResultModal({
        isOpen: true,
        type: "error",
        title: "Operation Failed",
        message: errorMessage,
      });
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = (domainId: number, domainName: string) => {
    setConfirmModal({ isOpen: true, domainId, domainName });
  };

  const handleConfirmDelete = async () => {
    if (!selectedAssessment || !confirmModal.domainId) return;

    const domainName = confirmModal.domainName;
    setConfirmModal({ isOpen: false, domainId: null, domainName: "" });

    setLoading(true);
    try {
      const response = await deleteDomain(confirmModal.domainId);
      if (response.success) {
        setResultModal({
          isOpen: true,
          type: "success",
          title: "Domain Removed Successfully",
          message: `Domain "${domainName}" has been removed from this assessment.`,
        });
        loadDomainsForAssessment(selectedAssessment.assessmentId);
      } else {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Unable to Remove Domain",
          message:
            "An error occurred while removing the domain. Please try again.",
        });
      }
    } catch (error) {
      setResultModal({
        isOpen: true,
        type: "error",
        title: "Operation Failed",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmModal({ isOpen: false, domainId: null, domainName: "" });
  };

  // Handlers for list view (All domains table)
  const handleEditDomainFromList = (domain: AssessmentDomain) => {
    setEditModal({
      isOpen: true,
      domainId: domain.id,
      domainName: domain.domain_name,
      assessmentId: domain.assessment_id,
    });
  };

  const handleDeleteDomainFromList = (domainId: number, domainName: string) => {
    setConfirmModal({ isOpen: true, domainId, domainName });
  };

  const handleConfirmDeleteFromList = async () => {
    if (!confirmModal.domainId) return;

    const domainName = confirmModal.domainName;
    setConfirmModal({ isOpen: false, domainId: null, domainName: "" });

    setLoading(true);
    try {
      const response = await deleteDomain(confirmModal.domainId);
      if (response.success) {
        setResultModal({
          isOpen: true,
          type: "success",
          title: "Domain Deleted Successfully",
          message: `Domain "${domainName}" has been removed.`,
        });
        loadAllDomains();
      } else {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Unable to Delete Domain",
          message:
            "An error occurred while deleting the domain. Please try again.",
        });
      }
    } catch (error) {
      setResultModal({
        isOpen: true,
        type: "error",
        title: "Operation Failed",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEditedDomain = async () => {
    if (!editModal.domainId || !editModal.domainName.trim()) return;

    const oldName = allDomains.find((d) => d.id === editModal.domainId)?.domain_name || "";
    const newName = editModal.domainName.trim();

    setEditModal({ isOpen: false, domainId: null, domainName: "", assessmentId: null });

    setLoading(true);
    try {
      const response = await updateDomain(editModal.domainId, newName);
      if (response.success) {
        setResultModal({
          isOpen: true,
          type: "success",
          title: "Domain Updated Successfully",
          message: `Domain "${oldName}" has been updated to "${newName}".`,
        });
        // Reload based on current view
        if (viewMode === "list") {
          loadAllDomains();
        } else if (selectedAssessment) {
          loadDomainsForAssessment(selectedAssessment.assessmentId);
        }
      } else {
        setResultModal({
          isOpen: true,
          type: "error",
          title: "Unable to Update Domain",
          message:
            "An error occurred while updating the domain. Please try again.",
        });
      }
    } catch (error) {
      setResultModal({
        isOpen: true,
        type: "error",
        title: "Operation Failed",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditModal({ isOpen: false, domainId: null, domainName: "", assessmentId: null });
  };

  return (
    <Layout>
      <div className="manage-domains-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        {/* Global Loading Overlay */}
        {(loading || addingDomain) && (
          <div className="domains-loading-overlay">
            <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
          </div>
        )}

        {viewMode === "list" ? (
          // LIST VIEW: All Domains Table
          <>
            {/* Header with Add Domain Button */}
            <div className="domains-header">
              <div>
                <h1>Manage Assessment Domains</h1>
                <p>
                  Control which email domains can access specific assessments
                </p>
              </div>
              <button
                className="btn-primary"
                onClick={() => setViewMode("add")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 3v10M3 8h10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Add Domain
              </button>
            </div>

            {/* Search Bar */}
            <div className="search-bar-section">
              <div className="search-input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="9"
                    cy="9"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M14 14l4 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by domain name or assessment title..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                />
                {searchTerm && (
                  <button
                    className="clear-search"
                    onClick={() => setSearchTerm("")}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4L12 12M12 4L4 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="results-count">
                Showing {paginatedDomains.length} of {filteredDomains.length}{" "}
                domain{filteredDomains.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Domains Table */}
            {loading ? (
              <div className="loading-spinner">Loading domains...</div>
            ) : filteredDomains.length > 0 ? (
              <>
                <div className="domains-table-wrapper">
                  <table className="domains-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Domain Name</th>
                        <th>Assessment Name</th>
                        <th>Added On</th>
                        <th className="action-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedDomains.map((domain, index) => (
                        <tr key={domain.id}>
                          <td>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="domain-name">{domain.domain_name}</td>
                          <td>{domain.assessmentTitle || "N/A"}</td>
                          <td>
                            {new Date(domain.created_at).toLocaleDateString()}
                          </td>
                          <td className="action-column">
                            <button
                              className="edit-domain-btn"
                              onClick={() => handleEditDomainFromList(domain)}
                              title="Edit domain"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8 3H3C2.4 3 2 3.4 2 4v11c0 .6.4 1 1 1h11c.6 0 1-.4 1-1v-5M13.5 2.5c.6-.6 1.5-.6 2.1 0 .6.6.6 1.5 0 2.1L9 11H7v-2l6.5-6.5z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              className="delete-domain-btn"
                              onClick={() =>
                                handleDeleteDomainFromList(
                                  domain.id,
                                  domain.domain_name,
                                )
                              }
                              title="Delete domain"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 5h10M7 8v4M11 8v4M5 5l1 9c0 .6.4 1 1 1h4c.6 0 1-.4 1-1l1-9M7 5V3c0-.6.4-1 1-1h2c.6 0 1 .4 1 1v2"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-info">
                      Page {currentPage} of {totalPages}
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="32"
                    cy="32"
                    r="30"
                    stroke="#CBD5E0"
                    strokeWidth="2"
                    strokeDasharray="4 4"
                  />
                  <path
                    d="M32 20v24M20 32h24"
                    stroke="#CBD5E0"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <h4>
                  {searchTerm ? "No domains found" : "No Domains Added Yet"}
                </h4>
                <p>
                  {searchTerm
                    ? "Try adjusting your search terms"
                    : "Add email domains to grant automatic access to users from specific organizations."}
                </p>
                <button
                  className="btn-primary"
                  onClick={() => setViewMode("add")}
                >
                  Add Domains Now
                </button>
              </div>
            )}
          </>
        ) : (
          // ADD DOMAIN VIEW: Assessment Selector + Add Form
          <>
            {/* Header with Back Button */}
            <div className="domains-header">
              <div>
                <button className="back-btn" onClick={handleBackToList}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L6 10L12 16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Back to All Domains
                </button>
                <h1>Add Assessment Domain</h1>
                <p>Select an assessment and add allowed email domains</p>
              </div>
            </div>

            {/* Assessment Selector */}
            <div className="assessment-selector-section">
              <label className="selector-label">Select Assessment</label>
              <div className="search-select-wrapper">
                <input
                  type="text"
                  className="assessment-search"
                  placeholder={
                    selectedAssessment
                      ? selectedAssessment.assessmentTitle
                      : "Click to select an assessment..."
                  }
                  value={assessmentSearchTerm}
                  onChange={(e) => setAssessmentSearchTerm(e.target.value)}
                  onFocus={() => setIsDropdownOpen(true)}
                  onClick={() => setIsDropdownOpen(true)}
                />
                {selectedAssessment ? (
                  <svg
                    className="clear-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={handleClearSelection}
                  >
                    <title>Clear selection</title>
                    <circle
                      cx="10"
                      cy="10"
                      r="9"
                      fill="currentColor"
                      opacity="0.1"
                    />
                    <path
                      d="M6 6L14 14M14 6L6 14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className="dropdown-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {isDropdownOpen && filteredAssessments.length > 0 && (
                  <div className="assessment-dropdown">
                    {filteredAssessments.map((assessment) => (
                      <div
                        key={assessment.assessmentId}
                        className={`dropdown-item ${selectedAssessment?.assessmentId === assessment.assessmentId ? "selected" : ""}`}
                        onClick={() => handleAssessmentSelect(assessment)}
                      >
                        <div className="dropdown-item-title">
                          {assessment.assessmentTitle}
                        </div>
                        <div className="dropdown-item-meta">
                          <span>{assessment.course}</span>
                          <span className={`status-badge ${assessment.status}`}>
                            {assessment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!selectedAssessment && (
                <div className="select-placeholder">
                  <p>Select an assessment to manage domain-based eligibility</p>
                  <p className="total-count">
                    {assessments.length} assessment
                    {assessments.length !== 1 ? "s" : ""} available
                  </p>
                </div>
              )}
            </div>

            {/* Selected Assessment Section */}
            {selectedAssessment && (
              <div className="selected-assessment-section">
                {/* Assessment Info Card */}
                <div className="assessment-info-card">
                  <div className="info-header">
                    <h2>{selectedAssessment.assessmentTitle}</h2>
                    <span
                      className={`status-badge ${selectedAssessment.status}`}
                    >
                      {selectedAssessment.status}
                    </span>
                  </div>
                  <div className="info-details">
                    <div className="info-item">
                      <span className="info-label">Course:</span>
                      <span className="info-value">
                        {selectedAssessment.course}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Allowed Domains:</span>
                      <span className="info-value highlight">
                        {domains.length} domain{domains.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                  <button
                    className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
                    onClick={() => setActiveTab("view")}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 5h12M2 9h8M2 13h10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    View Domains
                  </button>
                  <button
                    className={`tab-btn ${activeTab === "add" ? "active" : ""}`}
                    onClick={() => setActiveTab("add")}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 3v10M3 8h10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Add Domains
                  </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                  {activeTab === "view" && (
                    <div className="view-domains-tab">
                      <h3>Allowed Email Domains</h3>
                      {loading ? (
                        <div className="loading-spinner">
                          Loading domains...
                        </div>
                      ) : domains.length > 0 ? (
                        <>
                          <div className="domains-info-box">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="10"
                                cy="10"
                                r="9"
                                stroke="#3182CE"
                                strokeWidth="2"
                              />
                              <path
                                d="M10 6v4M10 14h.01"
                                stroke="#3182CE"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div>
                              Users with email addresses from these domains will
                              automatically have access to this assessment.
                            </div>
                          </div>
                          <div className="domains-table-wrapper">
                            <table className="domains-table">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Domain Name</th>
                                  <th>Added On</th>
                                  <th className="action-column">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {domains.map((domain, index) => (
                                  <tr key={domain.id}>
                                    <td>{index + 1}</td>
                                    <td className="domain-name">
                                      {domain.domain_name}
                                    </td>
                                    <td>
                                      {new Date(
                                        domain.created_at,
                                      ).toLocaleDateString()}
                                    </td>
                                    <td className="action-column">
                                      <button
                                        className="edit-domain-btn"
                                        onClick={() => handleEditDomainFromList(domain)}
                                        title="Edit domain"
                                      >
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M8 3H3C2.4 3 2 3.4 2 4v11c0 .6.4 1 1 1h11c.6 0 1-.4 1-1v-5M13.5 2.5c.6-.6 1.5-.6 2.1 0 .6.6.6 1.5 0 2.1L9 11H7v-2l6.5-6.5z"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                      <button
                                        className="delete-domain-btn"
                                        onClick={() =>
                                          handleRemoveDomain(
                                            domain.id,
                                            domain.domain_name,
                                          )
                                        }
                                        title="Remove domain"
                                      >
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M4 5h10M7 8v4M11 8v4M5 5l1 9c0 .6.4 1 1 1h4c.6 0 1-.4 1-1l1-9M7 5V3c0-.6.4-1 1-1h2c.6 0 1 .4 1 1v2"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className="empty-state">
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="32"
                              cy="32"
                              r="30"
                              stroke="#CBD5E0"
                              strokeWidth="2"
                              strokeDasharray="4 4"
                            />
                            <path
                              d="M32 20v24M20 32h24"
                              stroke="#CBD5E0"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          <h4>No Domains Added Yet</h4>
                          <p>
                            Add email domains to grant automatic access to users
                            from specific organizations.
                          </p>
                          <button
                            className="btn-primary"
                            onClick={() => setActiveTab("add")}
                          >
                            Add Domains Now
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "add" && (
                    <div className="add-domains-tab">
                      <h3>Add Email Domains</h3>

                      {/* Single Domain Input */}
                      <div className="add-method-card">
                        <div className="method-header">
                          <h4>Add Single Domain</h4>
                          <span className="method-badge">Quick Add</span>
                        </div>
                        <p className="method-description">
                          Enter an email domain (e.g., @example.com,
                          @company.com)
                        </p>
                        <input
                          type="text"
                          className={`domain-input ${singleDomainError ? "error" : ""}`}
                          placeholder="@example.com"
                          value={singleDomain}
                          onChange={(e) => handleSingleDomainChange(e.target.value)}
                          disabled={addingDomain}
                        />
                        {singleDomainError && (
                          <div className="input-error-message">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="8" cy="8" r="7" fill="#FEE2E2" />
                              <path
                                d="M8 4v5M8 11h.01"
                                stroke="#EF4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            {singleDomainError}
                          </div>
                        )}
                        <button
                          className="btn-primary"
                          onClick={handleAddSingleDomain}
                          disabled={!singleDomain.trim() || addingDomain || !!singleDomainError}
                        >
                          {addingDomain ? "Adding..." : "Add Domain"}
                        </button>
                      </div>

                      {/* Divider */}
                      <div className="divider">
                        <span>OR</span>
                      </div>

                      {/* Bulk Domains Input */}
                      <div className="add-method-card">
                        <div className="method-header">
                          <h4>Add Multiple Domains</h4>
                          <span className="method-badge">Bulk Add</span>
                        </div>
                        <p className="method-description">
                          Enter multiple email domains separated by commas or
                          new lines
                        </p>
                        <textarea
                          className="domain-input"
                          placeholder="@example.com, @company.com&#10;or one domain per line"
                          value={bulkDomains}
                          onChange={(e) => setBulkDomains(e.target.value)}
                          rows={5}
                          disabled={addingDomain}
                        />
                        <button
                          className="btn-primary"
                          onClick={handleBulkAddDomains}
                          disabled={!bulkDomains.trim() || addingDomain}
                        >
                          {addingDomain ? "Adding..." : "Add Domains"}
                        </button>
                      </div>

                      {/* Info Note */}
                      <div className="info-note">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="10"
                            cy="10"
                            r="9"
                            stroke="#3182CE"
                            strokeWidth="2"
                          />
                          <path
                            d="M10 6v4M10 14h.01"
                            stroke="#3182CE"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div>
                          <strong>Note:</strong> Users with email addresses from
                          these domains will automatically see this assessment,
                          even if they're not in the specific eligibility list.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Confirmation Modal */}
        {confirmModal.isOpen && (
          <div className="modal-overlay" onClick={handleCancelDelete}>
            <div
              className="confirmation-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="24"
                    cy="24"
                    r="22"
                    fill="#FEE2E2"
                    stroke="#EF4444"
                    strokeWidth="2"
                  />
                  <path
                    d="M24 16v12M24 32h.01"
                    stroke="#EF4444"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
                <h3>{viewMode === "list" ? "Delete Domain" : "Remove Domain"}</h3>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to {viewMode === "list" ? "delete" : "remove"}{" "}
                  <strong>{confirmModal.domainName}</strong>?
                </p>
                <p className="modal-warning">
                  Users from this domain will no longer have automatic access.
                </p>
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleCancelDelete}>
                  Cancel
                </button>
                <button
                  className="btn-confirm-delete"
                  onClick={viewMode === "list" ? handleConfirmDeleteFromList : handleConfirmDelete}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 4h10M6 7v4M10 7v4M4 4l1 9c0 .6.4 1 1 1h4c.6 0 1-.4 1-1l1-9M6 4V2c0-.6.4-1 1-1h2c.6 0 1 .4 1 1v2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {viewMode === "list" ? "Delete Domain" : "Remove Domain"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result Modal */}
        {resultModal.isOpen && (
          <div
            className="modal-overlay"
            onClick={() => setResultModal({ ...resultModal, isOpen: false })}
          >
            <div className="result-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                {resultModal.type === "success" ? (
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="24"
                      cy="24"
                      r="22"
                      fill="#D4EDDA"
                      stroke="#48BB78"
                      strokeWidth="2"
                    />
                    <path
                      d="M14 24l8 8 16-16"
                      stroke="#48BB78"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="24"
                      cy="24"
                      r="22"
                      fill="#FEE2E2"
                      stroke="#EF4444"
                      strokeWidth="2"
                    />
                    <path
                      d="M18 18l12 12M30 18l-12 12"
                      stroke="#EF4444"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                <h3>{resultModal.title}</h3>
              </div>
              <div className="modal-body">
                <p>{resultModal.message}</p>
              </div>
              <div className="modal-actions">
                <button
                  className={
                    resultModal.type === "success" ? "btn-success" : "btn-error"
                  }
                  onClick={() =>
                    setResultModal({ ...resultModal, isOpen: false })
                  }
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Domain Modal */}
        {editModal.isOpen && (
          <div className="modal-overlay" onClick={handleCancelEdit}>
            <div
              className="edit-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="24"
                    cy="24"
                    r="22"
                    fill="#DBEAFE"
                    stroke="#3B82F6"
                    strokeWidth="2"
                  />
                  <path
                    d="M16 22h-2c-.6 0-1 .4-1 1v13c0 .6.4 1 1 1h13c.6 0 1-.4 1-1v-2M25.5 15.5c.6-.6 1.5-.6 2.1 0 .6.6.6 1.5 0 2.1L18 27h-3v-3l10.5-10.5z"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h3>Edit Domain</h3>
              </div>
              <div className="modal-body">
                <label className="modal-label">Domain Name</label>
                <input
                  type="text"
                  className="modal-input"
                  value={editModal.domainName}
                  onChange={(e) =>
                    setEditModal({ ...editModal, domainName: e.target.value })
                  }
                  placeholder="@example.com"
                  autoFocus
                />
                <p className="modal-hint">
                  Enter the new name for this email domain
                </p>
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveEditedDomain}
                  disabled={!editModal.domainName.trim()}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L6 9L3 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ManageAssessmentDomains;
